'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Service } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Breadcrumbs } from '../ui/breadcrumbs';
import { Paragraph } from '../ui/paragraph';
import { Heading } from '../ui/heading';
import { PriceOfferDialog } from '../ui/price-offer-dialog';
import { GoArrowRight } from 'react-icons/go';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ServiceHeader({ service }: { service: Service }) {
  const { t } = useTranslations();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [sectionHeight, setSectionHeight] = useState(0);

  useEffect(() => {
    if (!sectionRef.current) return;

    const updateHeight = () => {
      setSectionHeight(sectionRef.current!.offsetHeight || 0);
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleScrollDown = () => {
    window.scrollTo({
      top: sectionHeight,
      behavior: 'smooth',
    });
  };

  return (
    <section ref={sectionRef} className={cn('grid w-full grid-cols-1 gap-0 md:grid-cols-2')}>
      <div
        className={cn(
          'col-span-1 flex h-full w-full flex-col items-end justify-start gap-6 py-8 pl-5 pr-5 sm:py-10 lg:py-14 lg:pr-16',
        )}
      >
        <div className="flex h-full w-full flex-col items-start justify-between gap-6 md:max-w-[600px]">
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Naše služby', href: '/#nase-sluzby' },
              { label: service.name, href: service.slug },
            ]}
          />
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <h1 data-ea-visual-tag="h2"
              className={cn(
                'text-5xl font-semibold leading-[1] tracking-tighter text-black sm:text-6xl sm:leading-[1] lg:text-[64px] lg:leading-[1]',
              )}
            >
              {service.name}
            </h1>
            <Paragraph
              innerHtml={service.description}
              color="muted-foreground"
              tag="div"
              className="w-full max-w-[540px]"
              size="md"
            />
          </div>
          <div
            className="flex w-full cursor-pointer items-center gap-2.5 transition-all hover:opacity-75"
            onClick={handleScrollDown}
          >
            <Image
              src={'/images/icons/arrow-circle-down.svg'}
              alt={t('text792')}
              width={20}
              height={20}
              className="object-contain"
            />
            <Paragraph size="md" color="primary">
              {t('text792')}
            </Paragraph>
          </div>
        </div>
      </div>
      <div className="relative col-span-1 flex aspect-square w-full items-end justify-start sm:aspect-auto sm:h-[600px] md:h-[500px] lg:h-[730px]">
        <PriceOfferDialog>
          <div className="relative z-10 flex w-full cursor-pointer flex-row items-center justify-between gap-4 bg-[#061E72] p-4 sm:max-w-[420px] sm:p-6 lg:gap-8 lg:p-8">
            <Heading
              title="Chcem cenovú ponuku na mieru"
              tag="h4"
              className="w-full max-w-[250px] text-balance text-white sm:max-w-[275px]"
            />
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
              <GoArrowRight size={20} />
            </div>
          </div>
        </PriceOfferDialog>
        <Image
          src={service?.images?.[0]?.url ?? ''}
          alt={resolveImageAlt(service?.images?.[0], service?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          loading="eager"
          sizes="100vw"
        />
      </div>
    </section>
  );
}
